import React, { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { Instructor, InstructorRole } from "../model/instructor";
import { ApiProvider, ApiProviderContext } from '../api/ApiProvider';

export enum UserStatus {
    LoggedIn,
    ToRegister,
    ToMigrate,
    None
}

interface AuthContextType {
    isLeader: boolean;
    user: Instructor | null;
    userStatus: UserStatus | null;

    authUser: (instructor : Instructor) => void;
    outUser: () => void;
    addJWTToken: (token: string) => void;
    getJWTToken: () => string;
}

interface AuthProviderProps {
    children: ReactNode;
}


const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    
  const [providerState, setProviderState] = useState<ApiProvider>(new ApiProvider());

  const [jwtToken, setJwtToken] = useState<string>(
    sessionStorage.getItem('jwtToken') ?? ''
  );

  const fetchedUserString = sessionStorage.getItem('user');
  const fetchedUser = fetchedUserString ? JSON.parse(fetchedUserString) : null;
  const [user, setUser] = useState<Instructor | null>(fetchedUser);
  const [userId, setUserId] = useState<string | null>(null);
  const [userStatus, setUserStatus] = useState<UserStatus | null>(null);
  
  useEffect(() => sessionStorage.setItem('jwtToken', jwtToken), [jwtToken]);
  useEffect(() => setProviderState(new ApiProvider(jwtToken)), [jwtToken]);
  useEffect(() => sessionStorage.setItem('user', JSON.stringify(user)), [user]);

  useEffect(() => {
      const queryParameters = new URLSearchParams(window.location.search);
      const param = queryParameters.get("code");
      if (param! == null || param! == '') return;
      if (param) {
          providerState.googleLogin(param!)
              .then(token => addJWTToken(token?? ""))
              .catch(err => console.error(err));
      }
  }, []);
  
  useEffect(() => {
      if (jwtToken == "" || jwtToken == 'null') return;
      providerState.getAccountStatus()
          .then(status => {
              setUserStatus(status);
              console.log("status: " + status);
          })
          .catch(err => console.error(err));
  }, [providerState]);
  
  useEffect(() => {
      console.log("changed userStatus to: " + userStatus);
      if (userStatus == UserStatus.None) { return }
      if (jwtToken == "" || jwtToken == 'null') return;
      providerState.getID()
          .then(id => setUserId(id))
          .catch(err => console.error(err));
  }, [userStatus]);

  useEffect(() => {
      if (userId == null || userId == '') return;
      providerState.getInstructor()
          .then(data => setUser(data))
          .catch(err => console.error(err));
  }, [userId]);

  const authUser = (instructor : Instructor) : void => {
    instructor.DateOfBirth = instructor.DateOfBirth.split('T')[0];
    if (instructor.Id) {
      setUser(instructor);
    }
  }

  const addJWTToken = (token: string) : void => {
    setJwtToken(token);
  }

  const getJWTToken = () : string => {
    return jwtToken!;
  }

  const outUser = () => {
    setJwtToken('');
    setUserStatus(UserStatus.None);
    setUser(null);
    sessionStorage.clear();
  };

  const isLeader = (user?.InstructorRole === InstructorRole.Leader);


  return (
    <AuthContext.Provider value={{ 
      isLeader: isLeader, user, userStatus,
      authUser, outUser, addJWTToken, getJWTToken
    }}>
        <ApiProviderContext.Provider value={providerState}>
            {children}
        </ApiProviderContext.Provider>
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};